import { ACCESS_PACKAGE, type AccessPackageCode } from '@choco-admin/types';
import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
} from '@heroui/react';
import React, { useCallback, useState } from 'react';

import { AlertCard } from '../../../../../components/AlertCard';
import { useNotification } from '../../../../../contexts/NotificationContext';
import {
  ACCESS_PACKAGE_FLAG,
  useAccessPackageAccess,
} from '../../../../../featureFlags';
import { useUpdateSupplierAccessPackage } from '../../../hooks/useUpdateSupplierAccessPackage';

import {
  ACCESS_PACKAGE_LABELS,
  accessPackageLabel,
  isSelectableAccessPackage,
  SELECTABLE_ACCESS_PACKAGES,
} from './accessPackageOptions';

interface AccessPackageSectionProps {
  supplierId?: string;
  /** The stored package. Absent reads as Free, which is what legacy displayed. */
  accessPackage?: string | null;
}

/**
 * A distributor's access package, and the one control that changes it.
 *
 * Separate from every other setting on this tab because the write is separate:
 * `accessPackage` is not on `SupplierUpdateInput` at all, so it cannot travel with
 * Save. It has its own mutation and it takes effect immediately, which is why this
 * is a modal with its own confirm rather than a field on the form.
 *
 * Two flags, both the legacy admin's: the section is hidden entirely without
 * `viewAccessPackage`, and the Change button needs `adminAccessPackageRights`.
 * Viewing which tier a distributor is on is not the same right as moving it.
 */
const AccessPackageControl: React.FC<AccessPackageSectionProps> = ({
  supplierId,
  accessPackage,
}) => {
  const { canChange } = useAccessPackageAccess();
  const { addNotification } = useNotification();
  const { updateAccessPackage, isSaving } = useUpdateSupplierAccessPackage();

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState<string>(
    accessPackage ?? ACCESS_PACKAGE.FREE
  );

  const open = useCallback(() => {
    // Start from what is stored, so closing without confirming changes nothing.
    setSelected(accessPackage ?? ACCESS_PACKAGE.FREE);
    setIsOpen(true);
  }, [accessPackage]);

  const confirm = useCallback(async () => {
    if (!supplierId) return;

    try {
      await updateAccessPackage(supplierId, selected as AccessPackageCode);
      setIsOpen(false);
      addNotification({
        title: 'Access package changed',
        message: `This distributor is now ${accessPackageLabel(selected)}.`,
        type: 'success',
      });
    } catch (error) {
      addNotification({
        title: 'Access package not changed',
        // The backend's own reason. It knows why it refused and this screen does
        // not, so replacing it with something generic would lose the only thing
        // the reader can act on.
        message:
          error instanceof Error
            ? error.message
            : 'The access package could not be changed.',
        type: 'error',
      });
    }
  }, [supplierId, selected, updateAccessPackage, addNotification]);

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between gap-6">
        <div>
          <p className="text-sm font-medium text-foreground">Access package</p>
          <p className="mt-1 text-xs text-default-600">
            Which Choco features this distributor has. Changing it takes effect
            immediately, without saving the form.
          </p>
        </div>
        <div className="flex flex-shrink-0 items-center gap-3">
          <span
            className="text-sm text-foreground"
            data-testid="supplier-access-package"
          >
            {accessPackageLabel(accessPackage)}
          </span>
          {canChange && supplierId && (
            <Button
              type="button"
              size="sm"
              variant="light"
              onPress={open}
              data-testid="change-access-package"
            >
              Change
            </Button>
          )}
        </div>
      </div>

      {!canChange && (
        <AlertCard
          type="info"
          title="Read-only for you"
          message={`To change a distributor's access package, ask for the ${ACCESS_PACKAGE_FLAG} feature flag to be switched on for you.`}
        />
      )}

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} size="sm">
        <ModalContent>
          <ModalHeader className="flex flex-col gap-1">
            <span>Change access package</span>
            <span className="text-sm font-normal text-default-600">
              This applies as soon as you confirm. It is not part of Save
              Changes.
            </span>
          </ModalHeader>
          <ModalBody>
            {!isSelectableAccessPackage(accessPackage) && (
              <p className="mb-3 text-sm text-default-600">
                This distributor is on {accessPackageLabel(accessPackage)},
                which is no longer offered. Moving it off cannot be undone from
                here.
              </p>
            )}
            <RadioGroup
              value={selected}
              onValueChange={setSelected}
              aria-label="Access package"
            >
              {SELECTABLE_ACCESS_PACKAGES.map((value) => (
                <Radio key={value} value={value}>
                  {ACCESS_PACKAGE_LABELS[value]}
                </Radio>
              ))}
            </RadioGroup>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="bordered"
              onPress={() => setIsOpen(false)}
              isDisabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              color="primary"
              onPress={confirm}
              isLoading={isSaving}
              isDisabled={selected === (accessPackage ?? ACCESS_PACKAGE.FREE)}
              data-testid="confirm-access-package"
            >
              Change
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

/**
 * The gate, kept separate from the body on purpose.
 *
 * Hooks cannot be called conditionally, so a single component would have to reach
 * for the notification context before it knows whether it renders anything, and
 * then demand a provider from every screen that merely mounts this tab. The gate
 * reads one flag and nothing else.
 */
export const AccessPackageSection: React.FC<AccessPackageSectionProps> = (
  props
) => {
  const { canView } = useAccessPackageAccess();

  if (!canView) return null;

  return <AccessPackageControl {...props} />;
};
